import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';

/**
 * Report a caught, non-fatal exception (e.g. from a try/catch branch),
 * so features can log to Sentry without importing the SDK directly.
 */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Report a standalone message without an Error object, e.g. an unexpected
 * but handled state that is still worth monitoring.
 */
export function reportMessage(message: string, level: SeverityLevel = 'info', context?: Record<string, unknown>): void {
  Sentry.captureMessage(message, { level, ...(context ? { extra: context } : {}) });
}

/**
 * Leave a breadcrumb that gets attached to later events, helping to
 * reconstruct the steps that led up to an error.
 */
export function addBreadcrumb(
  message: string,
  options?: { category?: string; level?: SeverityLevel; data?: Record<string, unknown> }
): void {
  Sentry.addBreadcrumb({ message, level: 'info', ...options });
}

/**
 * Associate subsequent events with the signed-in user (call after login).
 * Only the user id is sent to Sentry — no name, email, or other PII.
 * `isTokenUser` distinguishes shared-link/token sessions from regular logins.
 */
export function identifyUser(userId: string | number, isTokenUser?: boolean): void {
  Sentry.setUser({ id: String(userId) });
  Sentry.setTag('user.type', isTokenUser ? 'token' : 'regular');
}

/** Remove the user association from subsequent events (call on logout). */
export function clearUser(): void {
  Sentry.setUser(null);
  Sentry.setTag('user.type', undefined);
}
